import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';

import type {FirebaseTokenProvider} from 'services/Firebase/FirebaseTokenProvider';

import {DEFAULT_API_CONFIG} from './axiosConfig';
import {setupInterceptors} from './axiosInterceptors';

class ApiClientSingleton {
  private static instance: ApiClientSingleton | null = null;
  private axiosInstance: AxiosInstance | null = null;
  private tokenProvider: FirebaseTokenProvider | null = null;

  public static getInstance(): ApiClientSingleton {
    if (!ApiClientSingleton.instance) {
      ApiClientSingleton.instance = new ApiClientSingleton();
    }
    return ApiClientSingleton.instance;
  }

  public initialize(tokenProvider: FirebaseTokenProvider, config: AxiosRequestConfig = {}) {
    this.tokenProvider = tokenProvider;
    const mergedConfig = {
      ...DEFAULT_API_CONFIG,
      ...config,
    };
    this.axiosInstance = axios.create(mergedConfig);
    setupInterceptors(this.axiosInstance, this.tokenProvider);
    return this.axiosInstance;
  }

  public getClient(): AxiosInstance {
    if (!this.axiosInstance || !this.tokenProvider) {
      throw new Error('API client not initialized. Call initialize with token provider first.');
    }
    return this.axiosInstance;
  }
}

export const apiClient = ApiClientSingleton.getInstance();

/**
 * This export maintains backward compatibility with existing code that directly imports
 * and uses 'ApiAxios'. The Proxy ensures that all calls are delegated to the properly
 * initialized singleton instance while maintaining the same interface.
 *
 * Instead of requiring all existing code to be updated to use apiClient.getClient(),
 * this allows continued use of:
 * import {ApiAxios} from './axiosClient';
 * ApiAxios.get('/endpoint');
 *
 * The Proxy intercepts all property access and method calls, forwarding them to the
 * actual axios instance managed by our singleton. This provides a seamless transition
 * path while we gradually migrate to the new pattern.
 *
 * @deprecated Consider migrating to direct usage of apiClient.getClient() for better
 * type safety and explicit initialization requirements.
 */
export const ApiAxios = new Proxy({} as AxiosInstance, {
  get: (_target, prop: keyof AxiosInstance) => {
    const client = apiClient.getClient();
    return client[prop];
  },
});

export const initializeApi = (tokenProvider: FirebaseTokenProvider, config: AxiosRequestConfig = {}) => {
  return apiClient.initialize(tokenProvider, config);
};
